import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from "@angular/common/http";
import { EFileStatus } from "../interfaces/file-status.enum";
import { EFileType } from "../interfaces/file-type.enum";
import { getFileType, isDangerousFile } from "./file-helpers";
import { FileResourceHelper } from "@app/kernel/shared/helpers/file-resource-helper";
import { filter, map, Subject, takeUntil } from "rxjs";

export class FileModel {
  public extension: string;
  public type?: EFileType;
  public id: string;

  constructor(
    public key: string,
    public name: string,
    public original: File | undefined,
    public state: EFileStatus = EFileStatus.LOCAL,
    public percentage = 0,
    public base64: string | ArrayBuffer | null = "",
    readonly __name = "",
    public size = 0
  ) {
    this.size = this.original?.size ?? this.size ?? 0;
    this.extension = this.name.split(".")[this.name.split(".").length - 1];
    this.type = getFileType(this.extension);
    this.id = (10 * Math.random()).toString(36).substring(2);
    // this.getFileSize(this.original, this.key).then(s => (this.size = s ? +s : 0));
    if (!this.__name) {
      this.__name = name;
    }
  }

  /**
   * Returns the original file name without any modifications.
   */
  public get originalName() {
    return this.__name;
  }

  /**
   * Creates a new JSFile object with different name.
   * @param file
   * @param similar
   * @private
   */
  private static createJsFileWithNewName(file: File, similar: number): File {
    const name = file.name.split(".");
    const ext = name.length > 1 ? name.pop() : "";
    return new File([file], `${name.join(".")} [${similar}].${ext}`, { type: file.type });
  }

  /**
   * Creates the file model using a js file object
   * @param file
   * @param similar
   */
  static async createFromJsFile(file: File, similar = 0) {
    const __name = file.name;
    if (similar) {
      file = FileModel.createJsFileWithNewName(file, similar);
    }
    return new FileModel("", file.name, file, EFileStatus.LOCAL, 0, await FileModel.toBase64(file), __name);
  }

  /**
   * Creates the file model using a saved file key.
   * @param file
   */
  static createFromSavedFile(key: string) {
    return new FileModel(key, key.split("/")[key.split("/").length - 1], undefined, EFileStatus.SAVED, 100);
  }

  static createFileContent(key: string, http: HttpClient, cancelProcessFile$: Subject<void>) {
    return new Promise<FileModel>((resolve, reject) => {
      return http
        .head(FileResourceHelper(key), { observe: "response" })
        .pipe(
          takeUntil(cancelProcessFile$),
          filter((response: HttpResponse<unknown>) => Boolean(response?.headers.get("Content-Type")))
        )
        .subscribe({
          next: res => resolve(FileModel.createFromHttpHeaders(key, res.headers)),
          error: _ => {
            const file = FileModel.createErroredFileFromKey(key);
            reject(file);
          },
        });
    });
  }

  static createErroredFileFromKey(key: string) {
    return new FileModel(key, key.split("/")[key.split("/").length - 1], undefined, EFileStatus.ERRORED, 100);
  }

  static createFromHttpHeaders(key: string, headers: HttpHeaders | Headers) {
    const contentDisposition = headers.get("Content-Disposition") ?? "";
    const contentLength = headers.get("Content-Length");
    const fileName = contentDisposition?.split("filename=")[1];
    return new FileModel(key, JSON.parse(fileName) ?? "", undefined, EFileStatus.SAVED, 100, null, undefined, contentLength ? +contentLength : 0);
  }

  /**
   * Convert File to base 64 to render it on the screen;
   * @param file
   * @private
   */
  static async toBase64(file: File): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader?.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Returns true if the file is dangerous
   */
  isDangerous() {
    return isDangerousFile(this.name, this.extension ?? "");
  }

  /**
   * Updates the file upload progress using an HttpEvent
   * @param event
   */
  updateUploadStatus(event: HttpEvent<any>): void {
    if (event.type === HttpEventType.UploadProgress) {
      this.percentage = Math.round((event.loaded / event.total!) * 100);
    }
    if (event.type !== HttpEventType.Response) {
      return;
    }
    if (event.body.state && event.body.files.length) {
      this.state = EFileStatus.UPLOADED;
      // Find a record in the response with the same file name and get its key.
      this.key = event.body.files.find((file: any) => {
        // Fix encoding in case of the file name contains none english letters.
        const bytes = new Uint8Array([...file.name].map(c => c.charCodeAt(0)));
        return new TextDecoder("utf-8").decode(bytes) === this.name;
      })?.key;
    } else {
      this.state = EFileStatus.ERRORED;
    }
  }

  // manage file name if the value is array or string //
  static getFileName(value: string | string[]) {
    if (Array.isArray(value)) {
      return value.map(file => file.split("/")[file.split("/").length - 1].split("_").slice(1).join("_"));
    }
    return value.split("/")[value.split("/").length - 1].split("_").slice(1).join("_");
  }

  /**
   * Checks if file matches a maxSize and a type
   * @param maxSize
   * @param type
   */
  validate(maxSize: number, type: string): boolean {
    if (this.state === EFileStatus.SAVED) {
      return true;
    }
    if (maxSize && this.size > maxSize) {
      return false;
    }
    if (type && !this.isType(type)) {
      return false;
    }
    return true;
  }

  /**
   * Checks if the file matches a type or a group of types.
   * Allowed types should be separated by a ( , )
   * ex: `application/pdf,image/png`
   * @param type
   * @private
   */
  private isType(type: string): boolean {
    for (let _type of type.split(",")) {
      const __type = _type.trim().split("/");
      if (this.type === __type?.[0].toLowerCase() || this.extension === (__type?.[1] ?? __type?.[0].substring(1)).toLowerCase()) {
        return true;
      }
    }
    return false;
  }
}
